import React, { useCallback, useEffect, useState } from 'react'
import {
  BoltIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { policzStringi } from '../../api/elektryka'
import { odczytajJakoFormularz, zapiszParametry } from '../../lib/parametryElektryczne'
import { podsumujZestaw } from '../../lib/podsumowanieZestawu'
import { warunkiDoboru, wartoscWarunku } from '../../lib/warunkiDoboru'
import {
  etykietaWarunku, jednostkaWarunku, ostrzezeniePradu, ostrzezeniePrzyWarunku, uzupelnijDymki,
} from './tlumaczenieWarunkow'
import SchematStringow from './SchematStringow'
import ModalSchematu from './ModalSchematu'
import FormularzBrakow from './FormularzBrakow'
import ZamiennikFalownika from './ZamiennikFalownika'

/**
 * Dobór elektryczny dla pary moduł + falownik z dobranego zestawu.
 *
 * Rozwijany na żądanie (patrz `KitAssistant`) — większości użytkowników to nie
 * interesuje, a instalatorowi jest niezbędne. Liczenie startuje dopiero po
 * rozwinięciu, żeby nie obciążać backendu przy każdym doborze zestawu.
 */

/**
 * Jeden sprawdzony warunek: nazwa, wartość i znak, czy się mieści.
 *
 * Zielona fajka zamiast pary „wartość / granica" — użytkownik asystenta pyta
 * „czy to zagra", a nie „ile dokładnie zostało zapasu". Granice pokazuje dopiero
 * VoltValue Projekt, na osiach, gdzie widać je bez liczenia w pamięci.
 */
const Warunek = ({ warunek }) => {
  const { t } = useTranslation('elektryka')
  // Etykieta i jednostka przychodzą z `lib` po polsku; tłumaczymy je przy wyświetlaniu.
  const krotka = etykietaWarunku(t, warunek.krotka)
  // Dymek zamiast osobnego pudełka pod schematem: wyjaśnienie stoi tam, gdzie
  // pytanie („czemu ten trójkąt?"), a nie akapit niżej. `tabIndex` + `focus`,
  // bo bez tego treść byłaby dostępna wyłącznie myszą.
  const zDymkiem = warunek.podpowiedz && !warunek.spelniony

  const wartosc = (
    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums
                      ${warunek.spelniony ? 'text-gray-900' : 'text-amber-700'}
                      ${zDymkiem ? 'cursor-help underline decoration-dotted decoration-amber-400 underline-offset-4' : ''}`}
          tabIndex={zDymkiem ? 0 : undefined}
          aria-label={zDymkiem ? `${krotka}: ${warunek.podpowiedz}` : undefined}>
      {warunek.spelniony
        ? <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        : <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />}
      {wartoscWarunku({ ...warunek, jednostka: jednostkaWarunku(t, warunek.jednostka) })}
    </span>
  )

  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500">{krotka}</span>
      {zDymkiem
        ? (
          <span className="relative group/dymek focus-within:z-10">
            {wartosc}
            <span role="tooltip"
                  className="pointer-events-none absolute right-0 bottom-full mb-2 w-60 rounded-lg
                             bg-gray-900 dark:bg-gray-700 px-3 py-2 text-xs font-normal leading-5 text-white shadow-lg
                             opacity-0 group-hover/dymek:opacity-100 group-focus-within/dymek:opacity-100
                             transition-opacity z-20">
              {warunek.podpowiedz}
            </span>
          </span>
          )
        : wartosc}
    </div>
  )
}

const Komunikat = ({ ton, Ikona, children }) => (
  <div className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm leading-6 ${ton}`}>
    <Ikona className="w-4.5 h-4.5 flex-shrink-0 mt-0.5" style={{ width: 18, height: 18 }} />
    <div className="min-w-0">{children}</div>
  </div>
)

const PanelElektryczny = ({
  panelModelId, inverterModelId, mocDocelowaKwp, items = null, summary = null,
  dymekDoLewej = true,
  // Składniki kosztu z karty zestawu — potrzebne ostatniej stronie dokumentu,
  // żeby pokazała tę samą kwotę co karta, a nie policzoną drugi raz.
  koszty = null,
}) => {
  const { t } = useTranslation('elektryka')
  const [dane, setDane] = useState(null)
  const [blad, setBlad] = useState('')
  const [ladowanie, setLadowanie] = useState(true)
  const [orientacje, setOrientacje] = useState(1)
  // Ręcznie wpisane parametry. Przy 12% pokrycia katalogu to ścieżka GŁÓWNA,
  // nie awaryjna — dlatego formularz jest tuż pod listą braków, a nie schowany.
  // Start z tego, co już kiedyś wpisano dla tej pary produktów.
  const [reczne, setReczne] = useState(
    () => odczytajJakoFormularz({ panel: panelModelId, inverter: inverterModelId }),
  )
  const [modalOtwarty, setModalOtwarty] = useState(false)

  const licz = useCallback(async (ileOrientacji, wpisane = {}) => {
    setLadowanie(true)
    setBlad('')
    const doWyslania = { panel: {}, inverter: {} }
    Object.entries(wpisane).forEach(([klucz, wartosc]) => {
      if (wartosc === '' || wartosc == null) return
      const [strona, pole] = klucz.split('.')
      doWyslania[strona][pole] = Number(wartosc)
    })
    const res = await policzStringi({
      panel_model_id: panelModelId,
      inverter_model_id: inverterModelId,
      moc_docelowa_kwp: mocDocelowaKwp || null,
      orientacje: ileOrientacji,
      panel: doWyslania.panel,
      inverter: doWyslania.inverter,
    })
    setLadowanie(false)
    if (!res.ok) { setBlad(res.error); return }
    // Zapisujemy dopiero po udanym wyliczeniu — półwypełniony formularz
    // podstawiłby się na kolejnym ekranie i wyglądał jak komplet danych.
    if (res.data.ok) {
      zapiszParametry(wpisane, { panel: panelModelId, inverter: inverterModelId })
    }
    setDane(res.data)
  }, [panelModelId, inverterModelId, mocDocelowaKwp])

  useEffect(() => { licz(orientacje, reczne) }, [licz, orientacje])  // eslint-disable-line react-hooks/exhaustive-deps

  if (ladowanie && !dane) {
    return <p className="text-sm text-gray-500 py-4">{t('panel.laduje')}</p>
  }
  if (blad) {
    return (
      <Komunikat ton="bg-red-50 border-red-100 text-red-700" Ikona={XCircleIcon}>{blad}</Komunikat>
    )
  }
  if (!dane) return null

  // Brak danych katalogowych — mówimy DOKŁADNIE czego brakuje. Przy 12% pokrycia
  // modułów to nie jest ścieżka wyjątkowa, tylko częsta.
  if (!dane.ok && dane.braki?.length) {
    return (
      <FormularzBrakow
        braki={dane.braki}
        komunikat={dane.komunikat_pl}
        wartosci={reczne}
        onZmiana={(k, v) => setReczne((r) => ({ ...r, [k]: v }))}
        onLicz={() => licz(orientacje, reczne)}
        ladowanie={ladowanie}
      />
    )
  }

  const z = dane.zalecany
  const zestaw = podsumujZestaw(items, summary, z?.moc_kwp)
  // `uzupelnijDymki`: `lib` rozpoznaje ostrzeżenia tylko po polsku, a backend
  // tłumaczy je dla innych krajów (patrz tlumaczenieWarunkow).
  const warunki = uzupelnijDymki(warunkiDoboru(dane), dane.ostrzezenia)
  const maProblemPradu = (dane.ostrzezenia || []).some(ostrzezeniePradu)

  return (
    <div className="space-y-4">
      {/* Wybór orientacji — najczęstszy realny przypadek to wschód–zachód */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-600">{t('panel.uklad_dachu')}</span>
        {[{ v: 1, l: t('panel.jedna_polac') }, { v: 2, l: t('panel.wschod_zachod') }].map((o) => (
          <button
            key={o.v} type="button" onClick={() => setOrientacje(o.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              orientacje === o.v
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>

      {dane.bledy?.map((b, i) => (
        <Komunikat key={i} ton="bg-red-50 border-red-100 text-red-700" Ikona={XCircleIcon}>{b}</Komunikat>
      ))}

      {dane.ok && z && (
        <>
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
            <p className="text-sm font-bold text-blue-900 mb-0.5">
              {t('panel.naglowek', {
                count: z.stringow,
                n: z.modulow_w_stringu,
                lancuchy: t('liczby.lancuchy', { count: z.stringow }),
                moduly: t('liczby.moduly', { count: z.modulow_w_stringu }),
              })}
            </p>
            <p className="text-xs text-blue-700">
              {t('panel.podsumowanie', {
                razem: z.modulow_razem,
                razemModulow: t('liczby.moduly', { count: z.modulow_razem }),
                kwp: z.moc_kwp,
                uzyte: z.mppt_uzyte,
                wszystkie: dane.inverter.mppt_count,
              })}
            </p>
          </div>

          <SchematStringow uklad={z} mpptCount={dane.inverter.mppt_count}
                           nazwaFalownika={dane.inverter.nazwa}
                           mocAcKw={dane.inverter.ac_power_kw}
                           magazyn={zestaw?.magazyn} />

          <div className="flex flex-wrap items-start gap-2">
            <button type="button" onClick={() => setModalOtwarty(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-blue-200
                               bg-white text-blue-700 text-sm font-semibold hover:bg-blue-50 transition-colors">
              <DocumentArrowDownIcon className="w-4 h-4" />
              {zestaw?.kwp ? t('panel.otworz_projekt') : t('panel.otworz_schemat')}
            </button>

            {/* Wyjście z problemu, a nie sama diagnoza: pokazujemy się TYLKO gdy
                prąd faktycznie nie mieści się w wejściu. Bez tego użytkownik
                asystenta zostawał z komunikatem, na który nie ma jak zareagować —
                modułów po prądzie nie dobiera. */}
            {maProblemPradu && panelModelId && inverterModelId && (
              <ZamiennikFalownika
                panelModelId={panelModelId}
                inverterModelId={inverterModelId}
                mocDocelowaKwp={mocDocelowaKwp}
                orientacje={orientacje}
                doLewej={dymekDoLewej}
              />
            )}
          </div>

          <ModalSchematu otwarty={modalOtwarty} onZamknij={() => setModalOtwarty(false)}
                         dane={dane} zestaw={zestaw?.kwp ? zestaw : null} koszty={koszty} />

          {/* Sprawdzone warunki — sama odpowiedź „mieści się", bez widełek.
              „463 / 1100 V" zmuszało do liczenia w głowie, czy 463 to dużo, czy mało;
              kto chce zobaczyć zapas na osi, otwiera VoltValue Projekt. */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
              {t('panel.sprawdzone_warunki')}
            </p>
            <div className="grid sm:grid-cols-2 gap-x-6">
              {warunki.map((w) => <Warunek key={w.klucz} warunek={w} />)}
            </div>
          </div>
        </>
      )}

      {/* DC/AC i prąd pomijamy — ich wyjaśnienia stoją w dymkach przy samych
          warunkach (patrz `Warunek`), a pudełko powtarzałoby to zdanie obok tabelki. */}
      {dane.ostrzezenia?.filter((o) => !ostrzezeniePrzyWarunku(o)).map((o, i) => (
        <Komunikat key={i} ton="bg-amber-50 border-amber-100 text-amber-800"
                   Ikona={ExclamationTriangleIcon}>{o}</Komunikat>
      ))}

      {dane.zalozenia?.length > 0 && (
        <Komunikat ton="bg-gray-50 border-gray-200 text-gray-600" Ikona={InformationCircleIcon}>
          <p className="font-semibold mb-1">{t('panel.zalozenia')}</p>
          <ul className="list-disc list-inside space-y-0.5">
            {dane.zalozenia.map((z2, i) => <li key={i}>{z2}</li>)}
          </ul>
        </Komunikat>
      )}

      <p className="text-xs text-gray-400 leading-5 flex items-start gap-1.5">
        <BoltIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        {t('panel.stopka', { tmin: dane.warunki.t_min_c, tmax: dane.warunki.t_max_c })}
      </p>
    </div>
  )
}

export default PanelElektryczny
